import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { getPublicSetting } from '../utils/publicSettings';
import { getRecaptchaToken } from '../utils/recaptcha';

const InstagramIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
  </svg>
);

const PinterestIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.32-.09-.79-.17-2.01.04-2.88.19-.79 1.23-5.03 1.23-5.03s-.31-.63-.31-1.55c0-1.45.84-2.53 1.89-2.53.89 0 1.32.67 1.32 1.47 0 .9-.57 2.24-.87 3.48-.25 1.04.52 1.89 1.54 1.89 1.85 0 3.09-2.38 3.09-5.19 0-2.14-1.44-3.74-4.06-3.74-2.96 0-4.8 2.21-4.8 4.68 0 .85.25 1.45.64 1.91.18.21.2.3.14.55-.05.19-.16.63-.21.81-.07.26-.28.35-.51.25-1.43-.58-2.09-2.14-2.09-3.9 0-2.9 2.45-6.38 7.3-6.38 3.9 0 6.47 2.82 6.47 5.85 0 4-2.24 7-5.58 7-1.12 0-2.17-.6-2.53-1.28l-.7 2.68c-.21.79-.63 1.58-1 2.21.75.22 1.55.34 2.38.34 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
);

const WhatsAppIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.47 14.38c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.19.29-.75.95-.92 1.14-.17.2-.34.22-.63.07-.29-.15-1.22-.45-2.32-1.43-.86-.76-1.44-1.71-1.6-2-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.51-.17-.01-.37-.01-.56-.01-.2 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.44s1.05 2.83 1.19 3.03c.15.19 2.06 3.15 5 4.41.7.3 1.24.48 1.67.62.7.22 1.34.19 1.84.12.56-.08 1.72-.7 1.97-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.34z"/>
    <path d="M12.01 2C6.5 2 2 6.5 2 12.01c0 1.79.47 3.53 1.36 5.06L2 22l5.06-1.33a10 10 0 0 0 4.95 1.31h.01c5.51 0 10-4.5 10-10.01C22.02 6.5 17.52 2 12.01 2zm0 18.15h-.01c-1.55 0-3.06-.41-4.38-1.2l-.31-.19-3.26.85.87-3.18-.2-.32a8.15 8.15 0 0 1-1.25-4.3c0-4.51 3.67-8.18 8.18-8.18 4.51 0 8.18 3.67 8.18 8.18 0 4.51-3.67 8.34-8.32 8.34z"/>
  </svg>
);

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    whatsapp_number: '',
    website: '', // honeypot — real users leave this blank
  });
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const inputClass =
    'w-full bg-white border-2 border-secondary/40 focus:border-primary rounded-sm px-4 py-3 text-sm text-primary placeholder:text-secondary/70 placeholder:tracking-widest placeholder:text-xs outline-none transition-colors';

  const [contactInfo, setContactInfo] = useState({
    email: 'hello@lanresavage.com',
    phone: '+234 916 035 3402',
    address: 'Lagos, Nigeria',
    hours_weekday: '9:00 AM — 6:00 PM',
    hours_saturday: '10:00 AM — 4:00 PM',
    instagram: 'https://www.instagram.com/shoplanresavage?igsh=MXBpdDFoNnFkbWh6dw%3D%3D&utm_source=qr',
    pinterest: 'https://pin.it/45300tffQ',
    whatsapp: 'https://wa.me/2349160353402',
  });

  useEffect(() => {
    const fetchContactInfo = async () => {
      const [
        email, phone, address, hours_weekday, hours_saturday,
        instagram, pinterest, whatsapp,
      ] = await Promise.all([
        getPublicSetting('contact_email', contactInfo.email),
        getPublicSetting('contact_phone', contactInfo.phone),
        getPublicSetting('contact_address', contactInfo.address),
        getPublicSetting('contact_hours_weekday', contactInfo.hours_weekday),
        getPublicSetting('contact_hours_saturday', contactInfo.hours_saturday),
        getPublicSetting('social_instagram', contactInfo.instagram),
        getPublicSetting('social_pinterest', contactInfo.pinterest),
        getPublicSetting('social_whatsapp', contactInfo.whatsapp),
      ]);
      setContactInfo({
        email, phone, address, hours_weekday, hours_saturday,
        instagram, pinterest, whatsapp,
      });
    };
    fetchContactInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const recaptcha_token = await getRecaptchaToken('contact_form');
      await api.post('/api/contact', { ...formData, recaptcha_token });
      setSent(true);
      toast.success('Message sent successfully');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const socialLinks = [
    { label: 'Instagram', href: contactInfo.instagram, icon: <InstagramIcon /> },
    { label: 'Pinterest', href: contactInfo.pinterest, icon: <PinterestIcon /> },
    { label: 'WhatsApp', href: contactInfo.whatsapp, icon: <WhatsAppIcon /> },
  ];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="bg-light py-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-widest uppercase text-secondary font-medium mb-3">
            Get In Touch
          </p>
          <h1 className="font-serif text-5xl text-primary">Contact Us</h1>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-12"
          >
            <div>
              <p className="text-xs tracking-widest uppercase text-secondary font-medium mb-6">
                Our Details
              </p>
              <div className="space-y-6">
                {[
                  {
                    icon: <Mail size={16} />,
                    label: 'Email',
                    value: contactInfo.email,
                  },
                  {
                    icon: <Phone size={16} />,
                    label: 'Phone / WhatsApp',
                    value: contactInfo.phone,
                  },
                  {
                    icon: <MapPin size={16} />,
                    label: 'Address',
                    value: contactInfo.address,
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-start space-x-4">
                    <div className="text-nude mt-0.5">{item.icon}</div>
                    <div>
                      <p className="text-xs tracking-widest uppercase text-secondary font-medium mb-1">
                        {item.label}
                      </p>
                      <p className="text-sm text-primary">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs tracking-widest uppercase text-secondary font-medium mb-4">
                Customer Service Hours
              </p>
              <div className="space-y-2 text-xs text-secondary">
                <div className="flex justify-between">
                  <span>Monday — Friday</span>
                  <span>{contactInfo.hours_weekday}</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span>{contactInfo.hours_saturday}</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="text-nude">Closed</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs tracking-widest uppercase text-secondary font-medium mb-4">
                Follow Us
              </p>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                    className="text-nude hover:text-primary transition-colors"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {sent ? (
              <div className="flex flex-col items-center justify-center h-full space-y-6 text-center">
                <div className="w-16 h-16 bg-light rounded-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    strokeLinejoin="round" className="text-primary">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="font-serif text-3xl text-primary">
                  Message Sent
                </h2>
                <p className="text-xs text-nude tracking-wide leading-relaxed max-w-xs">
                  Thank you for reaching out. We will get back to you within
                  24 hours.
                </p>
                <button
                  onClick={() => {
                    setSent(false);
                    setFormData({ name: '', email: '', subject: '', message: '', whatsapp_number: '', website: '' });
                  }}
                  className="btn-secondary text-xs"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Honeypot field — invisible to real users, bots often fill every input */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  aria-hidden="true"
                />
                <p className="text-xs tracking-widest uppercase text-secondary font-medium mb-8">
                  Send a Message
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="YOUR NAME"
                    required
                    className={inputClass}
                  />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="EMAIL ADDRESS"
                    required
                    className={inputClass}
                  />
                </div>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="SUBJECT"
                  required
                  className={inputClass}
                />
                <div>
                  <input
                    type="tel"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    placeholder="WHATSAPP NUMBER (OPTIONAL)"
                    className={inputClass}
                  />
                  <p className="text-xs text-secondary mt-2">
                    Prefer to be reached on WhatsApp? Add your number and our team can follow up there directly.
                  </p>
                </div>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="YOUR MESSAGE"
                  required
                  rows={6}
                  className="w-full bg-white border-2 border-secondary/40 focus:border-primary rounded-sm px-4 py-3 text-sm text-primary placeholder:text-secondary/70 placeholder:tracking-widest placeholder:text-xs outline-none transition-colors resize-none"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;